import { useCallback, useEffect, useState } from 'react'

import { getActiveChainId } from '../../config'
import { getNativeCurrency } from '../../config/native'
import { Currency, JSBI, TokenAmount } from '../../sdk'
import { PairState, usePair } from '../../data/Reserves'
import { useAppWeb3 } from '../../hooks'
import { usePairAdder } from '../../state/user/hooks'
import { useTokenBalance } from '../../state/wallet/hooks'
import type { WidgetRouteIntent } from '../../widgets/navigation/widgetRouteIntent'
import { currencyId } from '../../utils/currencyId'

export enum ImportPoolField {
  TOKEN0 = 0,
  TOKEN1 = 1,
}

export function useImportPoolController(_resolveRoute: (intent: WidgetRouteIntent) => string) {
  void _resolveRoute
  const { account, chainId } = useAppWeb3()

  const [showSearch, setShowSearch] = useState(false)
  const [activeField, setActiveField] = useState<ImportPoolField>(ImportPoolField.TOKEN1)
  const [currency0, setCurrency0] = useState<Currency | null>(() => getNativeCurrency(getActiveChainId()))
  const [currency1, setCurrency1] = useState<Currency | null>(null)

  const [pairState, pair] = usePair(currency0 ?? undefined, currency1 ?? undefined)
  const addPair = usePairAdder()

  useEffect(() => {
    if (pair) {
      addPair(pair)
    }
  }, [pair, addPair])

  const validPairNoLiquidity =
    pairState === PairState.NOT_EXISTS ||
    Boolean(
      pairState === PairState.EXISTS &&
        pair &&
        JSBI.equal(pair.reserve0.raw, JSBI.BigInt(0)) &&
        JSBI.equal(pair.reserve1.raw, JSBI.BigInt(0)),
    )

  const position: TokenAmount | undefined = useTokenBalance(account ?? undefined, pair?.liquidityToken)
  const hasPosition = Boolean(position && JSBI.greaterThan(position.raw, JSBI.BigInt(0)))

  const openSearchForField = useCallback((field: ImportPoolField) => {
    setActiveField(field)
    setShowSearch(true)
  }, [])

  const handleCurrencySelect = useCallback(
    (currency: Currency) => {
      if (activeField === ImportPoolField.TOKEN0) {
        setCurrency0(currency)
      } else {
        setCurrency1(currency)
      }
      setShowSearch(false)
    },
    [activeField],
  )

  const handleSearchDismiss = useCallback(() => {
    setShowSearch(false)
  }, [])

  const addLiquidityHref =
    currency0 && currency1 && chainId
      ? currencyId(currency0, chainId) + '/' + currencyId(currency1, chainId)
      : ''

  return {
    ImportPoolField,
    account,
    activeField,
    addLiquidityCurrencyIds:
      currency0 && currency1 && chainId
        ? {
            token0: currencyId(currency0, chainId),
            token1: currencyId(currency1, chainId),
          }
        : null,
    addLiquidityHref,
    chainId,
    currency0,
    currency1,
    handleCurrencySelect,
    handleSearchDismiss,
    hasPosition,
    openSearchForField,
    pair,
    pairState,
    position,
    showSearch,
    validPairNoLiquidity,
  }
}

export type ImportPoolControllerResult = ReturnType<typeof useImportPoolController>
